import React, { useContext, useEffect, useState } from 'react';
import PageHeader from '../Common/PageHeader';
import {Container, Box, Button, CircularProgress, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { formattedNetworks } from '../../utils/Utils';
import { formattedSpanishNetworks } from '../../utils/FormattedSpanishNetwork';
import { useTranslation } from 'react-i18next';
import { ArrowForwardIosOutlined } from '@mui/icons-material';
import ErrorFallBackComponent from '../../utils/ErrorFallBackComponent';
import { MyContext } from '../../globalContext';
import PropTypes from 'prop-types';
import { getProviderSearchImage } from "../../data/Contentapi.js";

import DOMPurify from 'dompurify';
import trackClick from '../../data/ClickTracking.js';
import Constants from '../../utils/Constants.js';

const ChooseNetwork = ({ networkDataprop, errorprop, selSiteprop }) => {
    const [networkData, setNetworkData] = useState([]);
    const [data, setData] = useState({});
    const [loading] = useState(false);
    const [error, setError] = useState(false);
    const [clientImage, setClientImage] = useState('');
    const { setSelectedNetworkData, setSelectedSiteData, jwtToken, languageData } = useContext(MyContext);

    const networks = ['phcs', 'mpi', 'other'];
    const { t } = useTranslation();
    const [selectedNetwork, setSelectedNetwork] = useState("");

    const shouldGetNetworkImage = (selSitepropVal) => {
        return selSitepropVal && isValid(selSitepropVal.siteUrlSuffix);
    };
    
    useEffect(() => {
        setError(errorprop);
        initializeNetworkData();
        if (shouldGetNetworkImage(selSiteprop)) {
           
            getNetworkImage(selSiteprop.siteUrlSuffix);
        }
    }, [networkDataprop]);

    const initializeNetworkData = () => {
        if (!isValid(networkDataprop)) return;
        
        setNetworkData(networkDataprop);
        
        const networkDataFormatted = getFormattedNetworkData(networkDataprop, languageData);
        setData(networkDataFormatted);
    };
    
    const getFormattedNetworkData = (networkDatapropVal, languageDataVal) => {
        return languageDataVal === 'es'
            ? formattedSpanishNetworks(networkDatapropVal, t('noNetworkText'))
            : formattedNetworks(networkDatapropVal, t('noNetworkText'));
    };

    const isValid = (value) => value != null && value != "null" && value != "";

    const setNetwork = (event) => {
        const filteredNetworks = networkData.filter(net => net.networkStatementName.startsWith(event.target.value));

        const redirectUrl = getRedirectUrl(filteredNetworks);
        handleNetworkRedirect(filteredNetworks, redirectUrl, event.target.value);
    };

    const getRedirectUrl = (filteredNetworks) => {
        if (filteredNetworks.length === 0) return null;
        const network = filteredNetworks[0];
        let finalurl = "";

        if (network.redirectUrl && network.redirectUrl != "") {
			finalurl = network.redirectUrl ;
		}

		if (network.fullDirectoryURL && network.fullDirectoryURL != "") {
			finalurl = network.fullDirectoryURL ;
		}
		return finalurl;
    }

    

   

      const handleNetworkRedirect = (networksVal, rerirectUrl, networkValue) => {
        
            if (networksVal[0].networkStatementName === Constants.MULTIPLANMEDICARE) {
                window.alert(t("multiplanmedicare"));
            } else if (isValid(rerirectUrl)) {
                const userConfirmed = window.confirm(t("redirectmessage1") + networksVal[0].networkName + t("redirectmessage2"));
                if (userConfirmed) {
                    window.open(rerirectUrl, '_blank');
                }
            } else {
                setSelectedNetwork(networkValue);
            }
        };

    const getNetworkImage = async (clientsuffix) => {
        const imageData = await getProviderSearchImage(clientsuffix);
        if (!imageData) return;
    
        setClientImage(`data:${imageData.contentType};base64,${imageData.imageString}`);
    };

    const handleSearch = () => {
        const selectedNetworks = networkData.filter(net => net.networkStatementName.startsWith(selectedNetwork));
        
        if (selectedNetworks.length === 0) return;
        
        setSelectedSiteData(null);
        setSelectedNetworkData(selectedNetworks);
        trackClick('ChooseNetwork', 'Selected Network', selectedNetworks[0].networkName, languageData);
    };

    const isJwtTokenValid = (token) => Boolean(token);

    const shouldRenderSiteDetails = (selSitepropVal) => selSitepropVal?.siteName;

    const isLoading = (loadingVal) => {
        return loadingVal;
    };

    const shouldShowErrorFallback = (errorVal) => {
        return errorVal;
    };

    const hasNetworkData = (networkDataVal) => {
        return networkDataVal.length > 0;
    };

    const getDisplayStyle = (network, dataVal) => {
        return dataVal[network]?.value?.length > 0 ? "block" : "none";
    };

    const hasNetworkValue = (network, dataVal) => {
        return dataVal[network]?.value?.length > 0;
    };

    const isNetworkCodeIDontSeeOne = (networkCode) => {
        return networkCode === "IDONTSEEONE";
    };

    const renderNetworkSelection = () => {
        if (isLoading(loading)) {
            return (
                <Box alignItems="center" sx={{ marginX: "2.5%" }}>
                    <CircularProgress data-testid='loading-circle' color="primary" />
                </Box>
            );
        }
        if (shouldShowErrorFallback(error)) {
            return <ErrorFallBackComponent />;
        }
        if (hasNetworkData(networkData)) {
            return (
                <Box flexGrow={1} className="radio-input-div">
                    <FormControl sx={{ width: "100%" }}>
                        <RadioGroup
                            aria-labelledby="choose-network"
                            data-testid="network-radio-group"
                            value={selectedNetwork}
                            onChange={setNetwork}
                        >
                            <Grid container spacing={3} textAlign="left" marginLeft="58px">
                                {networks.map((network) => (
                                    <Grid
                                        item
                                        md={5}
                                        lg={4}
                                        key={network}
                                        className="responsive-width"
                                        style={{ display: getDisplayStyle(network, data) }}
                                    >
                                        <Box data-testid={`network-header${network}`}>
                                            {hasNetworkValue(network, data) && (
                                                <Typography component="span" fontWeight="bold" sx={{ ml: -1 }}>
                                                    {data[network].name}
                                                </Typography>
                                            )}
                                        </Box>
                                        {hasNetworkValue(network, data) &&
                                            data[network].value.map((item) => (
                                                <React.Fragment key={item.networkStatementName}>
                                                    {isNetworkCodeIDontSeeOne(item.networkCode) && <div><br /><br /></div>}
                                                    <FormControlLabel
                                                        key={item.networkStatementName}
                                                        value={item.networkStatementName}
                                                        control={
                                                            <Radio
                                                                id={`${network}-${item.networkStatementName}`}
                                                                data-testid={`${network}-${item.networkStatementName}`}
                                                                sx={{ padding: '3px', marginLeft: "-3px !important", '&.Mui-focusVisible': { color: '#007839' } }}
                                                                size="small"
                                                                inputProps={{ 'aria-label': `${item.networkStatementName}` }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography component="span" fontSize="14px">
                                                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.networkStatementName) }} />
                                                            </Typography>
                                                        }
                                                    />
                                                    <br />
                                                </React.Fragment>
                                            ))}
                                    </Grid>
                                ))}
                            </Grid>
                        </RadioGroup>
                    </FormControl>
                    <Box mt="20px" className="button-box">
                        <Button
                            variant="contained"
                            data-testid="select-and-search"
                            color="primary"
                            disabled={!selectedNetwork}
                            sx={{
                                textTransform: "initial",
                                borderRadius: "0px !important",
                                fontSize: "13pt",
                                paddingRight: "2%",
                                paddingLeft: "2%"
                            }}
                            className="search-btn"
                            onClick={handleSearch}
                        >
                            {t('selectAndSearchButton')}&nbsp;<ArrowForwardIosOutlined fontSize='14pt' />
                        </Button>
                    </Box>
                </Box>
            );
        }
        
    };

    return (
        <>
            {isJwtTokenValid(jwtToken) && (
                <Container sx={{ width: "100%", marginY: "2%" }} fixed>
                    <Box sx={{ width: "100%" }}>
                        <PageHeader displayText="chooseNetwork" />
                        {shouldRenderSiteDetails(selSiteprop) && (
                            <Box className="customized">
                                <Box sx={{ mb: 3 }}>{t('customizedfor')}</Box>
                                <Box sx={{ mb: 3 }}>
                                    <img src={clientImage} alt={selSiteprop.siteName} className="image-style" />
                                </Box>
                            </Box>
                        )}
                        {renderNetworkSelection()}
                    </Box>
                </Container>
            )}
        </>
    );
}

ChooseNetwork.propTypes = {
    networkDataprop: PropTypes.any,
    errorprop: PropTypes.any,
    selSiteprop: PropTypes.any
};

export default ChooseNetwork;